import { FC, useMemo, useState } from 'react';

import { Search2Line } from '@assets/icons';
import Collapse from '@components/Collapse/Collapse';
import MultiSelectionFilter from '@components/ui/filters/MultiSelectionFilter/MultiSelectionFilter';
import {
  ListingFilterItemInterface,
  ListingFilterItemKey,
} from '@components/ui/filters/MultiSelectionFilter/types';
import CustomIcon from '@components/utils/CustomIcon';
import { Row } from '@components/formStyles';

import { ClearAll } from './SelectionFilterStyle';
import {
  HotelFilterItemsWrapper,
  HotelFilterSectionContent,
  HotelFilterSectionSearchInput,
  HotelFilterSectionSearchWrapper,
  HotelFilterSectionTitle,
  HotelFilterSectionWrapper,
} from './HotelFilterSectionStyle';

interface HotelFilterSectionProps {
  title: string;
  data: ListingFilterItemInterface[];
  selectedItems: ListingFilterItemKey[];
  handleSelectedItems: (items: ListingFilterItemKey[]) => void;
  searchable?: boolean;
  collapsible?: boolean;
  handleRemoveItems?: () => void;
}

const VISIBLE_ITEMS_LIMIT = 5;
const SCROLLABLE_ITEMS_THRESHOLD = 10;

const HotelFilterSection: FC<HotelFilterSectionProps> = ({
  title,
  data,
  selectedItems,
  handleSelectedItems,
  searchable = true,
  collapsible = false,
  handleRemoveItems,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase('tr-TR');
    return normalizedSearch
      ? data.filter((item) =>
          item.value.toLocaleLowerCase('tr-TR').includes(normalizedSearch)
        )
      : data;
  }, [data, searchTerm]);

  const hasSelectionInSection = useMemo(
    () =>
      data.some((item) =>
        selectedItems?.some((key) => String(key) === String(item.key))
      ),
    [data, selectedItems]
  );

  if (!data.length) return null;

  const items = !isExpanded
    ? filteredItems.slice(0, VISIBLE_ITEMS_LIMIT)
    : filteredItems;
  const hasMore = filteredItems.length > VISIBLE_ITEMS_LIMIT;
  const content = (
    <HotelFilterSectionContent>
      {searchable ? (
        <HotelFilterSectionSearchWrapper>
          <CustomIcon icon={Search2Line} paletteColor='grey500' fontSize={20} />
          <HotelFilterSectionSearchInput
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder='Ara'
            aria-label={`${title} ara`}
          />
        </HotelFilterSectionSearchWrapper>
      ) : null}
      <HotelFilterItemsWrapper
        $isScrollable={
          isExpanded && filteredItems.length > SCROLLABLE_ITEMS_THRESHOLD
        }
      >
        <MultiSelectionFilter
          data={items}
          selectedItems={selectedItems}
          handleSelectedItems={handleSelectedItems}
          showSubTitle
          subTitleKey='count'
        />
      </HotelFilterItemsWrapper>
      {hasMore ? (
        <button type='button' onClick={() => setIsExpanded((value) => !value)}>
          {isExpanded ? 'Daha Az Göster' : 'Daha Fazla Göster'}
        </button>
      ) : null}
    </HotelFilterSectionContent>
  );

  return (
    <HotelFilterSectionWrapper>
      {collapsible ? (
        <Collapse
          defaultActiveKey={hasSelectionInSection ? 'hotel-filter' : undefined}
          panelProps={{
            header: <HotelFilterSectionTitle>{title}</HotelFilterSectionTitle>,
            key: 'hotel-filter',
          }}
        >
          {content}
        </Collapse>
      ) : (
        <>
          <Row alignItems='center' justifyContent='space-between'>
            <HotelFilterSectionTitle>{title}</HotelFilterSectionTitle>
            {handleRemoveItems && selectedItems.length ? (
              <ClearAll onClick={handleRemoveItems}>Tümünü Temizle</ClearAll>
            ) : null}
          </Row>
          {content}
        </>
      )}
    </HotelFilterSectionWrapper>
  );
};

export default HotelFilterSection;
